import React from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    makeStyles,
    Divider,
} from '@material-ui/core';
import { CheckMarkGreen, CrossMarkIcon } from '../../assets/svg';
import { LightBoxImage } from '../UI';
import {
    isDigilockerVerified,
    isImageLink,
    isBlueDartOrg,
    verifiedByDigilockerStatus,
} from '../../utils/appHelper';
import { PdfViewerButton } from '../Modals/PdfViewerModalV2';
import {
    APPROVE_STATUS,
    DL_COURT_CASE_INITIATION_STATUS,
    DL_COURT_CASE_REJECTED_STATUS,
    DOCUMENT_REJECTED_STATUS,
    DOCUMENT_STATUS,
} from '../../utils/appConstants';
import moment from 'moment';
import {
    ImageProcessingNotice,
    UnmaskedBadge,
    RevealButton,
    useRevealedImage,
    useRevealedNumber,
} from './reveal';

const useStyles = makeStyles((theme) => ({
    card: {
        padding: '16px',
        marginBottom: '16px',
        borderRadius: '12px',
        border: '1px solid rgba(38, 50, 56, 0.16)',
        backgroundColor: '#fff',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    title: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#14112d',
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    statusText: {
        fontSize: '14px',
        fontWeight: 600,
    },
    statusApproved: {
        color: '#4caf50',
    },
    statusRejected: {
        color: '#f44336',
    },
    statusPending: {
        color: '#ff9800',
    },
    fieldLabel: {
        fontSize: '13px',
        color: 'rgba(20, 17, 45, 0.64)',
        fontWeight: 500,
        marginBottom: '4px',
    },
    fieldValue: {
        fontSize: '14px',
        color: '#14112d',
        fontWeight: 600,
        marginBottom: '12px',
    },
    imageContainer: {
        display: 'flex',
        gap: '16px',
        marginTop: '12px',
    },
    imagePlaceholder: {
        width: '120px',
        height: '80px',
        borderRadius: '8px',
        border: '1px dashed rgba(38, 50, 56, 0.32)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        color: '#9e9e9e',
        fontSize: '12px',
    },
    actionContainer: {
        display: 'flex',
        gap: '12px',
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid rgba(38, 50, 56, 0.08)',
    },
    approveBtn: {
        color: '#4caf50',
        borderColor: '#4caf50',
        fontWeight: 600,
        '&:hover': {
            borderColor: '#388e3c',
            backgroundColor: 'rgba(76, 175, 80, 0.04)',
        },
    },
    rejectBtn: {
        color: '#f44336',
        borderColor: '#f44336',
        fontWeight: 600,
        '&:hover': {
            borderColor: '#d32f2f',
            backgroundColor: 'rgba(244, 67, 54, 0.04)',
        },
    },
    rejectReason: {
        marginTop: '8px',
        padding: '8px 12px',
        backgroundColor: '#ffebee',
        borderRadius: '6px',
    },
    rejectReasonLabel: {
        fontSize: '12px',
        color: '#f44336',
        fontWeight: 500,
    },
    rejectReasonText: {
        fontSize: '13px',
        color: '#d32f2f',
        fontWeight: 600,
    },
    digilockerInfo: {
        marginTop: '8px',
        padding: '12px',
        backgroundColor: '#e8f5e9',
        borderRadius: '8px',
    },
    noDataText: {
        color: '#9e9e9e',
        fontSize: '14px',
        fontStyle: 'italic',
    },
}));

export interface KYCDocument {
    id?: number | null;
    documentNo?: string | null;
    image?: string | null;
    imageFront?: string | null;
    imageBack?: string | null;
    approvalStatus?: 'APPROVED' | 'REJECTED' | null | string;
    rejectReason?: string | null;
    autoKycBroker?: string | null;
    autoKycDocumentNo?: string | null;
    autoKycDocumentName?: string | null;
    autoKycDocumentDob?: string | null;
    faceMatchConfidence?: string | null;
    faceMatchScore?: string | null;
    verified?: boolean;
    docDate?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    color?: string | null;
    registrationNo?: string | null;
    docDateMilliSeconds?: number | null;
    dlCaseCheck?: string | null;
    pdfDocumentLink?: string | null;
    profileImageLink?: string | null;
    revealable?: boolean | null;
    // Gates the IMAGE control specifically. Distinct from `revealable`, which gates the NUMBER:
    // an Aadhaar number is never revealable but its image is, so sharing one flag would make
    // Aadhaar image review impossible.
    imageRevealable?: boolean | null;
    imageUnavailable?: boolean | null;
    imageUnmasked?: boolean | null;
}

export interface KYCDocumentCardProps {
    title: string;
    document: KYCDocument | null;
    documentType: 'AADHAAR' | 'PAN' | 'DL' | 'RC';
    showBackImage?: boolean;
    onApprove?: (documentId: number) => void;
    onReject?: (documentId: number) => void;
    readOnly?: boolean;
    userStatus: string;
}

const getStatusInfo = (document: KYCDocument | null, documentType: string) => {
    if (!document || !document.id) {
        return {
            text: 'Not Submitted',
            status: 'NOT_SUBMITTED' as const,
        };
    }
    const isDLDocument = documentType === 'DL';
    const isPANDocument = documentType === 'PAN';
    const isVerifiedByDigilocker = isPANDocument
        ? document?.dlCaseCheck === 'VERIFIED' && isDigilockerVerified(document)
        : isDigilockerVerified(document);
    const isApproved = document?.approvalStatus === 'APPROVED';
    const isRejected = DOCUMENT_REJECTED_STATUS.includes(
        document?.approvalStatus || '',
    );
    const isPending = APPROVE_STATUS.includes(document?.approvalStatus || '');
    const isDLRejected = !!isPANDocument
        ? DL_COURT_CASE_REJECTED_STATUS.includes(document?.dlCaseCheck || '')
        : false;
    const isDLInitiated = !!isPANDocument
        ? DL_COURT_CASE_INITIATION_STATUS.includes(document?.dlCaseCheck || '')
        : false;

    if (isVerifiedByDigilocker) {
        return {
            text: 'Verified by DigiLocker',
            status: 'DIGILOCKER' as const,
        };
    }
    if (isApproved) {
        return {
            text: 'Verified',
            status: 'APPROVED' as const,
        };
    }
    if (!!isDLRejected) {
        return {
            text: 'Initiation Failed',
            status: 'REJECTED' as const,
        };
    }
    if (isRejected) {
        return {
            text:
                document?.approvalStatus === 'VENDOR_REJECTED'
                    ? 'Vendor Rejected'
                    : document?.approvalStatus === 'DIGILOCKER_REJECTED'
                      ? 'DigiLocker Rejected'
                      : document?.approvalStatus === 'BD_OPS_REJECTED'
                        ? 'OPS Rejected'
                        : 'Rejected',
            status: 'REJECTED' as const,
        };
    }
    if (isDLInitiated) {
        return {
            text: 'Initiated',
            canTakeAction: isPending,
            status: 'PENDING' as const,
        };
    }

    return {
        text: 'Pending Verification',
        canTakeAction: isPending,
        status: 'PENDING' as const,
    };
};

const KYCDocumentCard: React.FC<KYCDocumentCardProps> = ({
    title,
    document,
    documentType,
    showBackImage = true,
    onApprove,
    onReject,
    readOnly = false,
    userStatus,
}) => {
    const classes = useStyles();
    const statusInfo = getStatusInfo(document, documentType);
    const isVerifiedByDigilocker = document
        ? isDigilockerVerified(document)
        : false;
    const digilockerInfo = document
        ? verifiedByDigilockerStatus(document)
        : null;
    const isOtherDocumentFound = !!document?.pdfDocumentLink;

    const hasDocument = document && document?.id;
    const canTakeAction =
        !readOnly &&
        hasDocument &&
        !isVerifiedByDigilocker &&
        userStatus !== 'PENDING_VENDOR_APPROVAL' &&
        statusInfo?.canTakeAction;

    const getStatusClass = () => {
        switch (statusInfo.status) {
            case 'APPROVED':
            case 'DIGILOCKER':
                return classes.statusApproved;
            case 'REJECTED':
                return classes.statusRejected;
            default:
                return classes.statusPending;
        }
    };

    const getStatusIcon = () => {
        switch (statusInfo.status) {
            case 'APPROVED':
            case 'DIGILOCKER':
                return <CheckMarkGreen style={{ width: '18px' }} />;
            case 'REJECTED':
                return <CrossMarkIcon style={{ width: '18px' }} />;
            default:
                return null;
        }
    };

    const revealedNumber = useRevealedNumber(document?.id);
    const frontImageUrl = useRevealedImage(
        document?.id,
        document?.profileImageLink || document?.imageFront || document?.image,
    );

    const getDocumentNumber = () => {
        return document?.documentNo || document?.autoKycDocumentNo || '-';
    };

    const renderDocumentImage = (
        imageUrl: string | null | undefined,
        label: string,
    ) => {
        if (imageUrl) {
            return (
                <Box>
                    <Typography className={classes.fieldLabel}>
                        {label}
                    </Typography>
                    <LightBoxImage
                        source={imageUrl}
                        containerStyles={{
                            width: 120,
                            height: 80,
                        }}
                        imageStyles={{
                            width: 120,
                            height: 80,
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border: '1px solid rgba(38, 50, 56, 0.16)',
                        }}
                    />
                </Box>
            );
        }
        return (
            <Box>
                <Typography className={classes.fieldLabel}>{label}</Typography>
                <Box className={classes.imagePlaceholder}>No Image</Box>
            </Box>
        );
    };

    const handleApprove = () => {
        if (document?.id && onApprove) {
            onApprove(document.id);
        }
    };

    const handleReject = () => {
        if (document?.id && onReject) {
            onReject(document.id);
        }
    };

    return (
        <Box className={classes.card}>
            {/* Header */}
            <Box className={classes.cardHeader}>
                <Typography className={classes.title}>{title}</Typography>
                <Box className={classes.statusContainer}>
                    <Typography
                        className={`${classes.statusText} ${getStatusClass()}`}
                    >
                        {statusInfo.text}
                    </Typography>
                    {getStatusIcon()}
                </Box>
            </Box>

            {hasDocument ? (
                <>
                    {/* Document Details */}
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography className={classes.fieldLabel}>
                                Document Number
                            </Typography>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: 6 }}
                            >
                                <Typography className={classes.fieldValue}>
                                    {revealedNumber || getDocumentNumber()}
                                </Typography>
                                <RevealButton
                                    documentId={document?.id}
                                    documentType={documentType}
                                    revealable={document?.revealable}
                                    action="UNMASK_NUMBER"
                                    subject={`${title} number`}
                                />
                            </Box>
                        </Grid>

                        {isVerifiedByDigilocker && digilockerInfo?.name && (
                            <Grid item xs={6}>
                                <Typography className={classes.fieldLabel}>
                                    Name (as per DigiLocker)
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {digilockerInfo.name}
                                </Typography>
                            </Grid>
                        )}

                        {isVerifiedByDigilocker &&
                            digilockerInfo?.documentDob && (
                                <Grid item xs={6}>
                                    <Typography className={classes.fieldLabel}>
                                        Date of Birth
                                    </Typography>
                                    <Typography className={classes.fieldValue}>
                                        {digilockerInfo.documentDob}
                                    </Typography>
                                </Grid>
                            )}

                        {documentType === 'DL' && document?.docDate && (
                            <Grid item xs={6}>
                                <Typography className={classes.fieldLabel}>
                                    Date of Birth
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {moment(document?.docDate).format(
                                        'DD-MM-YYYY',
                                    )}
                                </Typography>
                            </Grid>
                        )}
                        {documentType === 'RC' && document?.docDate && (
                            <Grid item xs={6}>
                                <Typography className={classes.fieldLabel}>
                                    Manufacturer Date
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {moment(document?.docDate).format(
                                        'DD-MM-YYYY',
                                    )}
                                </Typography>
                            </Grid>
                        )}
                        {document?.manufacturer && (
                            <Grid item xs={6}>
                                <Typography className={classes.fieldLabel}>
                                    Manufacturer Name
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {document?.manufacturer}
                                </Typography>
                            </Grid>
                        )}
                        {document?.model && (
                            <Grid item xs={6}>
                                <Typography className={classes.fieldLabel}>
                                    Model
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {document?.model}
                                </Typography>
                            </Grid>
                        )}
                        {document?.color && (
                            <Grid item xs={6}>
                                <Typography className={classes.fieldLabel}>
                                    Color
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {document?.color}
                                </Typography>
                            </Grid>
                        )}
                        {(() => {
                            const faceMatchValue = isBlueDartOrg()
                                ? Number(document?.faceMatchScore) > 0
                                    ? document?.faceMatchScore
                                    : null
                                : document?.faceMatchConfidence;
                            return (
                                faceMatchValue != null && (
                                    <Grid item xs={6}>
                                        <Typography
                                            className={classes.fieldLabel}
                                        >
                                            Face Match Score
                                        </Typography>
                                        <Typography
                                            className={classes.fieldValue}
                                            style={{
                                                color:
                                                    Number(faceMatchValue) < 80
                                                        ? '#f44336'
                                                        : '#4caf50',
                                            }}
                                        >
                                            {faceMatchValue}
                                        </Typography>
                                    </Grid>
                                )
                            );
                        })()}
                    </Grid>

                    {/* Document Images */}
                    {!isVerifiedByDigilocker && document?.imageUnavailable && (
                        <ImageProcessingNotice />
                    )}
                    {!isVerifiedByDigilocker && document?.imageUnmasked && (
                        <Box style={{ marginBottom: 8 }}>
                            <UnmaskedBadge visible={document?.imageUnmasked} />
                        </Box>
                    )}
                    {!isVerifiedByDigilocker && (
                        <Box style={{ marginBottom: 8 }}>
                            <RevealButton
                                documentId={document?.id}
                                documentType={documentType}
                                revealable={document?.imageRevealable}
                                action="UNBLUR_IMAGE"
                                subject={`${title} image`}
                            />
                        </Box>
                    )}
                    {!isVerifiedByDigilocker && (
                        <Box className={classes.imageContainer}>
                            {renderDocumentImage(
                                frontImageUrl,
                                documentType === 'PAN'
                                    ? 'PAN Card'
                                    : document?.profileImageLink
                                      ? 'Aadhaar Photo'
                                      : 'Front Side',
                            )}
                            {showBackImage &&
                                documentType !== 'PAN' &&
                                !document?.profileImageLink &&
                                renderDocumentImage(
                                    document?.imageBack,
                                    'Back Side',
                                )}
                        </Box>
                    )}
                    {!!isVerifiedByDigilocker &&
                        !!(
                            isImageLink(document?.profileImageLink) ||
                            isImageLink(document?.imageFront) ||
                            isImageLink(document?.image)
                        ) && (
                            <Box className={classes.imageContainer}>
                                {renderDocumentImage(
                                    document?.profileImageLink ||
                                        document?.imageFront ||
                                        document?.image,
                                    documentType === 'PAN'
                                        ? 'PAN Card'
                                        : document?.profileImageLink
                                          ? 'Aadhaar Photo'
                                          : 'Front Side',
                                )}
                            </Box>
                        )}
                    {!!isOtherDocumentFound && (
                        <Box className={classes.imageContainer}>
                            <PdfViewerButton
                                file={document?.pdfDocumentLink}
                                variant="link"
                                linkText="Click to view PDF document from DigiLocker"
                            />
                        </Box>
                    )}

                    {/* Reject Reason */}
                    {document?.rejectReason && (
                        <Box className={classes.rejectReason}>
                            <Typography className={classes.rejectReasonLabel}>
                                Rejection Reason
                            </Typography>
                            <Typography className={classes.rejectReasonText}>
                                {document.rejectReason}
                            </Typography>
                        </Box>
                    )}

                    {/* Action Buttons */}
                    {canTakeAction && (
                        <Box className={classes.actionContainer}>
                            <Button
                                variant="outlined"
                                className={classes.approveBtn}
                                onClick={handleApprove}
                                disableElevation
                            >
                                Approve
                            </Button>
                            <Button
                                variant="outlined"
                                className={classes.rejectBtn}
                                onClick={handleReject}
                                disableElevation
                            >
                                Reject
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <Typography className={classes.noDataText}>
                    No {title.toLowerCase()} information submitted
                </Typography>
            )}
        </Box>
    );
};

export default KYCDocumentCard;
